import { AfterViewInit, Component, EventEmitter, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import moment from 'moment';
import { NotificationClassEnum } from '../../models/notification-class.enum';
import { CVANotificationService } from '../../services/cva-notification/cva-notification.service';
import { PatientService } from '../../services/patient/patient.service';
import { PatientResponse } from '../../models/patient.interface';
import { HttpErrorResponse } from '@angular/common/http';
import { PractitionerService } from '../../services/practitioner/practitioner.service';
import { PractitionerResponse } from '../../models/practitioner.interface';
import { SlotService } from '../../services/slots/slots.service';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger, MAT_AUTOCOMPLETE_SCROLL_STRATEGY } from '@angular/material/autocomplete';
import { Overlay } from '@angular/cdk/overlay';
import { AppointmentService } from '../../services/appointment/appointment.service';
import { Appointment } from '../../models/appointment.interface';
import { LookupService } from '../../services/lookup/lookup.service';
import { LookupTypeEnum } from '../../models/lookup-type.enum';
import { LookupEntryInterface } from '../../models/lookup.interface';
import { CVA_CONSTANTS } from '../../util/constants';
import { environment } from '../../../environments/environment';
import { finalize } from 'rxjs';

export interface SlotInterface {
  start: string;
  end: string;
  status: string;
  slotId: string
}

export function closeOnScrollStrategyFactory(overlay: Overlay) {
  return () => overlay.scrollStrategies.close();
}

@Component({
    standalone: false,
    selector: 'cva-book-appointment',
    templateUrl: './book-appointment.component.html',
    styleUrl: './book-appointment.component.scss',
    providers: [
        {
            provide: MAT_AUTOCOMPLETE_SCROLL_STRATEGY,
            useFactory: closeOnScrollStrategyFactory,
            deps: [Overlay]
        }
    ],
    
})
export class BookAppointmentComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatAutocompleteTrigger) providerAutocompleteTrigger?: MatAutocompleteTrigger;

  private readonly closeProviderAutocompleteOnScroll = () => {
    if (this.providerAutocompleteTrigger?.panelOpen) {
      this.providerAutocompleteTrigger.closePanel();
    }
  };

  @Output() closeBookingSection = new EventEmitter<void>();
  @Output() appointmentBooked = new EventEmitter<Date>();
  bookAppointmentForm: FormGroup = this.formBuilder.group({
    reason: [""],
    patient: ["", [Validators.required]],
    patientId: ["", [Validators.required]],
    provider: ["", [this.requireMatch.bind(this), Validators.required]],
    dob: ["", [Validators.required]],
    startDate: [new Date(), [Validators.required]],
    startTime: ["", [Validators.required]],
    duration: ["", [Validators.required]],
    appointmentType: ["", Validators.required],
    note: [""]
  });
  cvaUI_url = environment.cvaUI_url;
  appointmentTypes: LookupEntryInterface[] = [];
  slots: SlotInterface[] = [];
  selectedSlot: SlotInterface | undefined;
  filteredProviders: PractitionerResponse[] = [];
  referenceId: string | undefined = '';
  slotId: string = '';
  selectedPatient: PatientResponse | undefined;
  currentDate = new Date();
  noSlotsMessage: string = CVA_CONSTANTS.MESSAGE.NO_SLOTS_FOUND;
  isPatientValid = false;
  isScheduleClicked = false;
  isLoading = true;
  isScheduling = false;
  private lastSlotRequestKey = '';
  private slotRequestSequence = 0;
  private activeSlotRequestId = 0;

  constructor(
    private readonly formBuilder: FormBuilder,
    public readonly cvaNotificationService: CVANotificationService,
    public readonly patientService: PatientService,
    public readonly providerService: PractitionerService,
    public readonly appointmentService: AppointmentService,
    public readonly slotService: SlotService,
    public readonly lookupService: LookupService
  ) { }

  ngOnInit() {
    const providerId = localStorage.getItem("providerId") ?? '';
    this.lookupService.getLookupValuesByType(LookupTypeEnum.APPOINTMENT_TYPE).subscribe((appointmentTypeList) => {
      this.appointmentTypes = appointmentTypeList;
    });
    this.providerService.getPractitionerByHpiId(providerId).subscribe(() => {
      if (this.providerService?.loggedInPractitioner) {
        this.filteredProviders.push(this.providerService.loggedInPractitioner)
        const loggedInPractitionerFullName = `${this.providerService.loggedInPractitioner?.firstName} ${this.providerService.loggedInPractitioner?.lastName}`;
        this.provider?.setValue(loggedInPractitionerFullName);
      }
      this.referenceId = this.providerService.loggedInPractitioner?.referenceId;
      if (this.referenceId) {
        this.fillAvailableSlots(this.referenceId);
      }
    });
  }

  private requireMatch(control: FormControl): ValidationErrors | null {
    const selectedValue: string = control.value;
    if (Array.isArray(this.filteredProviders)) {
      for (const practitioner of this.filteredProviders) {
        const fullName = `${practitioner.firstName} ${practitioner.lastName}`;

        if (selectedValue === fullName) {
          return null;
        }
      }
    }
    return { requireMatch: true };
  }

  get reason(): AbstractControl | null {
    return this.bookAppointmentForm.get("reason");
  }

  get patient(): AbstractControl | null {
    return this.bookAppointmentForm.get("patient");
  }
  get patientId(): AbstractControl | null {
    return this.bookAppointmentForm.get("patientId");
  }
  get provider(): AbstractControl | null {
    return this.bookAppointmentForm.get("provider");
  }
  get dob(): AbstractControl | null {
    return this.bookAppointmentForm.get("dob");
  }
  get startDate(): AbstractControl | null {
    return this.bookAppointmentForm.get("startDate");
  }
  get startTime(): AbstractControl | null {
    return this.bookAppointmentForm.get("startTime");
  }
  get duration(): AbstractControl | null {
    return this.bookAppointmentForm.get("duration");
  }
  get appointmentType(): AbstractControl | null {
    return this.bookAppointmentForm.get("appointmentType");
  }
  get note(): AbstractControl | null {
    return this.bookAppointmentForm.get("note");
  }

  onHide() {
    this.closeBookingSection.emit();
  }

  fillAvailableSlots(referenceId: string, forceReload: boolean = false) {
    this.isLoading = true;
    this.slots = [];
    if (!this.startDate?.value) {
      this.isLoading = false;
      return
    }

    const startDate = moment(this.currentDate).isAfter(moment(this.startDate?.value).startOf('d')) ? this.currentDate.toISOString() : moment(this.startDate?.value).startOf('d').toISOString();
    const endDate = moment(startDate).endOf('d').toISOString();
    const requestKey = `${referenceId}|${startDate}|${endDate}`;

    if (!forceReload && this.lastSlotRequestKey === requestKey) {
      this.isLoading = false;
      return;
    }

    this.lastSlotRequestKey = requestKey;
    const requestId = ++this.slotRequestSequence;
    this.activeSlotRequestId = requestId;
    console.log("Fetching slots for selected date");
    this.slotService.getSlotsByProviderId(referenceId, startDate, endDate)
      .pipe(
        finalize(() => {
          if (requestId === this.activeSlotRequestId) {
            this.isLoading = false;
          }
        }),
      ).subscribe({
        next: (response: { response: SlotInterface[] }) => {
          if (requestId !== this.activeSlotRequestId) {
            return;
          }

          console.log("Slots fetched for selected date");
          if (response.response.length === 0) {
            this.cvaNotificationService.showNotification(
              CVA_CONSTANTS.MESSAGE.NO_SLOTS_FOUND,
              NotificationClassEnum.INFO
            );

          }
          this.slots = (response?.response || []).map(res => ({
            start: res.start,
            end: res.end,
            status: res.status,
            slotId: res.slotId
          }));
        },
        error: (errorResponse: HttpErrorResponse) => {
          if (requestId !== this.activeSlotRequestId) {
            return;
          }

          console.log("Slots fetch failure");
          let errorMessage: string = '';
          if (errorResponse.error?.error) {
            errorMessage = errorResponse.error.error.errorMessage;
          }
          this.cvaNotificationService.showNotification(
            errorMessage || CVA_CONSTANTS.MESSAGE.NO_SLOTS_FOUND,
            NotificationClassEnum.ERROR
          );
        }
      });
  }

  selectSlot(slot: SlotInterface) {
    if (slot.status === "free") {
      this.selectedSlot = slot;
      this.slotId = slot.slotId;
      this.startTime?.setValue(moment(slot.start).format("HH:mm"));
      this.duration?.setValue(moment(slot.end).diff(moment(slot.start), 'minutes') + " Min");
    }
  }

  scheduleAppointment() {
    if (this.isScheduling) {
      return;
    }

    this.isScheduleClicked = true;
    Object.keys(this.bookAppointmentForm.controls).forEach((controlName: string) => {
      this.bookAppointmentForm.get(controlName)?.markAsTouched();
    });

    if (this.bookAppointmentForm.valid) {
      this.isScheduling = true;
      const createAppointmentBody: Appointment = {
        'appointmentType': this.appointmentType?.value.toUpperCase(),
        'start': new Date((<SlotInterface>this.selectedSlot).start).toISOString(),
        'end': new Date((<SlotInterface>this.selectedSlot).end).toISOString(),
        'slotId': this.slotId,
        'notes': this.reason?.value,
        'practitionerId': this.referenceId,
        'patientId': this.selectedPatient?.referenceId,
        'status': 'Booked'
      }
      console.log("Booking an appointment");
      this.appointmentService.createAppointment(createAppointmentBody)
        .pipe(finalize(() => this.isScheduling = false))
        .subscribe({
        next: () => {
          console.log("Appointment booked");
          this.bookAppointmentForm.reset();
          this.slots = [];
          this.isScheduleClicked = false;
          this.cvaNotificationService.showNotification(
            CVA_CONSTANTS.MESSAGE.APPOINTMENT_BOOKED_SUCCESSFULLY,
            NotificationClassEnum.SUCCESS
          );
          console.log(CVA_CONSTANTS.MESSAGE.APPOINTMENT_BOOKED_SUCCESSFULLY);

          this.appointmentBooked.emit(new Date((<SlotInterface>this.selectedSlot).start));
        },
        error: (errorResponse) => {
          console.log("Book appointment failed");
          let errorMessage: string = '';
          if (errorResponse.error?.error) {
            errorMessage = errorResponse.error.error.errorMessage;
          }
          this.cvaNotificationService.showNotification(
            errorMessage || CVA_CONSTANTS.MESSAGE.CREATE_APPOINTMENT_ERR,
            NotificationClassEnum.ERROR
          );
          console.error(errorMessage);
        }
      });
    } else {
      this.cvaNotificationService.showNotification(CVA_CONSTANTS.MESSAGE.MISSING_MANDATORY_FIELDS, NotificationClassEnum.ERROR);
    }
  }

  invalidPatientId() {
    this.patientId?.setValue('')
    this.patient?.setValue('')
    this.dob?.setValue('');
    this.isPatientValid = false;
    this.cvaNotificationService.showNotification(
      CVA_CONSTANTS.MESSAGE.PATIENT_NOT_FOUND,
      NotificationClassEnum.ERROR
    );
  }

  patientUpdated() {
    this.isPatientValid = false;
  }

  searchPatientById() {
    const medicalRecordNumber = this.patientId?.value.trim();

    if (!medicalRecordNumber) {
      return;
    }
    console.log("Searching patient by id");
    this.patientService.getPatientById(medicalRecordNumber).subscribe({
      next: (response: { response: PatientResponse }) => {
        console.log("Patient found for given id");
        if (response.response?.dateOfBirth) {
          console.log(`Patient found successfully for patientId: ${medicalRecordNumber}.`);
          this.selectedPatient = response.response;
          this.patient?.setValue(`${response.response?.firstName} ${response.response?.lastName}`);
          this.dob?.setValue(new Date(response.response?.dateOfBirth));
          this.isPatientValid = true;
        } else {
          this.invalidPatientId();
        }
      },
      error: () => {
        console.log("Patient search by id failed");
        this.invalidPatientId();
      }
    });
  }

  searchProviderByName() {
    this.filteredProviders = [];
    if (!this.provider?.value || this.provider?.value?.length < 3) {
      return;
    }
    console.log("Searching provider by name");
    this.providerService.getPractitionerByName(this.provider?.value).subscribe({
      next: (response: { response: PractitionerResponse[] }) => {
        console.log("Provider found for given name");
        this.filteredProviders = response.response;
      },
      error: (errorResponse: HttpErrorResponse) => {
        console.log("Provider search by name failed");
        let errorMessage: string = '';
        if (errorResponse?.error?.error) {
          errorMessage = errorResponse.error.error.errorMessage;
        }
        this.cvaNotificationService.showNotification(
          errorMessage || CVA_CONSTANTS.MESSAGE.NO_PROVIDER,
          NotificationClassEnum.ERROR
        );
        console.error(errorMessage)
      }
    });
  }

  checkProvider() {
    if (!this.provider?.value || this.provider?.value?.length < 3) {
      return;
    }
    this.assignProvider(this.provider?.value)
  }

  selectedProvider(event: MatAutocompleteSelectedEvent) {
    this.assignProvider(event.option.value, true)
  }

  assignProvider(providerName: string, forceReload: boolean = false) {
    const selectedProvider = this.filteredProviders.find(provider => `${provider.firstName} ${provider.lastName}` === providerName);
    this.referenceId = selectedProvider?.referenceId;
    if (this.referenceId) {
      this.provider?.setErrors(null);
      this.fillAvailableSlots(this.referenceId, forceReload);
    }
  }

  updateSlots(forceReload: boolean = false) {
    this.startTime?.setValue('');
    this.duration?.setValue('');
    if (this.referenceId) {
      this.fillAvailableSlots(this.referenceId, forceReload)
    }
  }

  ngAfterViewInit() {
    window.addEventListener('wheel', this.closeProviderAutocompleteOnScroll, { passive: true });
    window.addEventListener('touchmove', this.closeProviderAutocompleteOnScroll, { passive: true });
    document.addEventListener('scroll', this.closeProviderAutocompleteOnScroll, true);
  }

  ngOnDestroy() {
    window.removeEventListener('wheel', this.closeProviderAutocompleteOnScroll);
    window.removeEventListener('touchmove', this.closeProviderAutocompleteOnScroll);
    document.removeEventListener('scroll', this.closeProviderAutocompleteOnScroll, true);
  }
}
